<template>
  <div class="min-h-screen bg-gradient-to-br from-green-50 to-cyan-100 p-4 md:p-6">
    <div class="max-w-7xl mx-auto">
      <WelcomeBanner />

      <!-- Main Content Container with Glassmorphism -->
      <div
        class="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl p-4 md:p-6 mt-6 border border-white/30"
      >
        <!-- Header Section -->
        <div
          class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4"
        >
          <div>
            <h1 class="text-2xl md:text-3xl font-bold text-gray-800">Farms Management</h1>
            <p class="text-gray-600 mt-1">Manage your agricultural properties efficiently</p>
          </div>
          <button
            @click="openCreateModal"
            class="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create New Farm
          </button>
        </div>

        <!-- Search and Filter Section -->
        <div class="bg-white/80 rounded-xl p-4 mb-6 shadow-sm border border-white/20">
          <div class="flex flex-col md:flex-row gap-4">
            <!-- Search Input -->
            <div class="flex-1">
              <label for="search" class="block text-sm font-medium text-gray-700 mb-1"
                >Search Farms</label
              >
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  id="search"
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search by farm name..."
                  class="pl-10 w-full rounded-lg border-gray-300 border focus:ring-2 focus:ring-green-500 focus:border-green-500 py-2 px-4 shadow-sm"
                />
              </div>
            </div>

            <!-- Status Filter -->
            <div class="w-full md:w-48">
              <label for="statusFilter" class="block text-sm font-medium text-gray-700 mb-1"
                >Filter by Status</label
              >
              <select
                id="statusFilter"
                v-model="statusFilter"
                class="w-full rounded-lg border-gray-300 border focus:ring-2 focus:ring-green-500 focus:border-green-500 py-2 px-4 shadow-sm bg-white"
              >
                <option :value="null">All Statuses</option>
                <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                  {{ status.label }}
                </option>
              </select>
            </div>

            <!-- Sort Direction -->
            <div class="w-full md:w-48">
              <label for="sortDirection" class="block text-sm font-medium text-gray-700 mb-1"
                >Sort Direction</label
              >
              <select
                id="sortDirection"
                v-model="sortDirection"
                class="w-full rounded-lg border-gray-300 border focus:ring-2 focus:ring-green-500 focus:border-green-500 py-2 px-4 shadow-sm bg-white"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Farms Table -->
        <FarmTable
          :farms="paginatedFarmsWithNumbers"
          @edit="openEditModal"
          @delete="handleDelete"
        />

        <!-- Pagination Controls -->
        <div
          class="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-gray-200"
        >
          <div class="text-sm text-gray-700 mb-4 sm:mb-0">
            Showing <span class="font-medium">{{ startIndex + 1 }}</span> to
            <span class="font-medium">{{ endIndex }}</span> of
            <span class="font-medium">{{ filteredFarms.length }}</span> results
          </div>

          <div class="flex space-x-2">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            <div class="flex items-center space-x-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-3 py-1 rounded-lg border transition-colors',
                  currentPage === page
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                ]"
              >
                {{ page }}
              </button>
              <span v-if="hasEllipsis" class="px-2">...</span>
            </div>

            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>

          <div class="mt-4 sm:mt-0">
            <label for="perPage" class="text-sm text-gray-700 mr-2">Rows per page:</label>
            <select
              id="perPage"
              v-model="itemsPerPage"
              class="rounded-lg border-gray-300 border focus:ring-2 focus:ring-green-500 focus:border-green-500 py-1 px-2 shadow-sm bg-white"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <FarmModal v-if="isModalOpen" :farm="editingFarm" @close="closeModal" @saved="handleSaved" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import FarmTable from '@/components/farm/FarmTable.vue'
import FarmModal from '@/components/farm/FarmModal.vue'
import { fetchFarmsAPI, deleteFarmAPI } from '@/api/farmService'
import type { Farm } from '@/types/Farm'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'

// Farm status options - using string values directly
interface StatusOption {
  value: string
  label: string
}

const statusOptions: StatusOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'RENTED_FROM', label: 'Rented from another person' },
  { value: 'RENTED_TO', label: 'Rented to another person' },
  { value: 'SOLD', label: 'Sold' },
]

// Reactive data
const farms = ref<Farm[]>([])
const isModalOpen = ref(false)
const editingFarm = ref<Farm | null>(null)
const searchQuery = ref('')
const statusFilter = ref<string | null>(null)
const sortDirection = ref<'asc' | 'desc'>('asc')
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Computed properties
const filteredFarms = computed(() => {
  let result = Array.isArray(farms.value) ? farms.value : []

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((farm) => farm.name.toLowerCase().includes(query))
  }

  // Apply status filter
  if (statusFilter.value) {
    result = result.filter((farm) => farm.status === statusFilter.value)
  }

  // Sort by status using the order defined in statusOptions
  result = Array.isArray(result)
    ? [...result].sort((a, b) => {
        const statusOrder = statusOptions.map((opt) => opt.value)
        const aIndex = statusOrder.indexOf(a.status)
        const bIndex = statusOrder.indexOf(b.status)

        const aValue = aIndex !== -1 ? aIndex : statusOrder.length
        const bValue = bIndex !== -1 ? bIndex : statusOrder.length

        return sortDirection.value === 'asc' ? aValue - bValue : bValue - aValue
      })
    : []

  return result
})

// Add numbering to farms for display
const paginatedFarmsWithNumbers = computed(() => {
  return paginatedFarms.value.map((farm, index) => ({
    ...farm,
    number: startIndex.value + index + 1,
  }))
})

const totalPages = computed(() => Math.ceil(filteredFarms.value.length / itemsPerPage.value))

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value)

const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage.value, filteredFarms.value.length),
)

const paginatedFarms = computed(() => filteredFarms.value.slice(startIndex.value, endIndex.value))

const visiblePages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const delta = 2
  const range = []
  const rangeWithDots: number[] = []

  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i)
  }

  if (current - delta > 2) {
    rangeWithDots.push(1)
  }
  if (current - delta > 3) {
    rangeWithDots.push(-1) // -1 represents ellipsis
  }

  rangeWithDots.push(...range)

  if (current + delta < total - 2) {
    rangeWithDots.push(-1) // -1 represents ellipsis
  }
  if (current + delta < total - 1) {
    rangeWithDots.push(total)
  }

  // Always include first and last page if not already included
  if (!rangeWithDots.includes(1)) rangeWithDots.unshift(1)
  if (!rangeWithDots.includes(total) && total > 1) rangeWithDots.push(total)

  return rangeWithDots
})

const hasEllipsis = computed(() => visiblePages.value.includes(-1))

// Methods
const fetchFarms = async () => {
  try {
    const res = await fetchFarmsAPI()
    if (!Array.isArray(res)) {
      console.warn(
        '[fetchFarms] API returned non-array, normalizing to empty array or wrapped data',
        res,
      )
      // If the API returns an object with a `results` or `data` key, attempt to extract safely
      const maybeObj: unknown = res
      if (maybeObj !== null && typeof maybeObj === 'object' && !Array.isArray(maybeObj)) {
        const obj = maybeObj as Record<string, unknown>
        if (Array.isArray(obj['results'])) farms.value = obj['results'] as unknown as Farm[]
        else if (Array.isArray(obj['data'])) farms.value = obj['data'] as unknown as Farm[]
        else farms.value = []
      } else {
        farms.value = []
      }
    } else {
      farms.value = res
    }
    console.log('API response:', farms.value)
  } catch (err) {
    console.error('[fetchFarms] failed to load farms', err)
    farms.value = []
  } finally {
    // Reset to first page when filters change
    currentPage.value = 1
  }
}

const openCreateModal = () => {
  editingFarm.value = null
  isModalOpen.value = true
}

const openEditModal = (farm: Farm) => {
  editingFarm.value = { ...farm }
  isModalOpen.value = true
}

const closeModal = () => {
  isModalOpen.value = false
}

const handleSaved = () => {
  closeModal()
  fetchFarms()
}

const handleDelete = async (farm: Farm) => {
  if (confirm(`Are you sure you want to delete farm: ${farm.name}?`)) {
    await deleteFarmAPI(farm.id)
    await fetchFarms()
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const goToPage = (page: number) => {
  if (page !== -1) {
    // Skip ellipsis
    currentPage.value = page
  }
}

// Watchers
watch([searchQuery, statusFilter, sortDirection], () => {
  currentPage.value = 1
})

watch(itemsPerPage, () => {
  currentPage.value = 1
})

// Lifecycle hooks
onMounted(fetchFarms)
</script>
